import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  PlugsConnected, ArrowClockwise, CheckCircle, WarningCircle, Buildings,
} from "@phosphor-icons/react";
import client from "../lib/api";

const STATUS_META = {
  connected:    { label: "Connected",    cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", icon: CheckCircle },
  expired:      { label: "Expired",      cls: "text-amber-300 border-amber-500/40 bg-amber-500/10", icon: WarningCircle },
  disconnected: { label: "Disconnected", cls: "text-neutral-400 border-white/15 bg-white/[0.03]", icon: WarningCircle },
};

/**
 * Admin view of every pilot's QuickBooks connection state.
 * Route: /app/admin/integrations
 */
export default function AdminIntegrationsPage() {
  const [conns, setConns] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/qbo/connections");
      setConns(data?.connections || []);
      setConfigured(data?.configured !== false);
    } catch (e) {
      toast.error("Could not load QBO connections");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const connectedCount = conns.filter((c) => c.status === "connected").length;

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]" data-testid="admin-integrations-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
            <PlugsConnected size={12} weight="fill" /> Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Integrations</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            Every pilot's QuickBooks connection state. Read-only — pilots connect + disconnect from their own Integrations page.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-integrations-refresh-btn"
          className="rounded-full px-4 py-2 text-sm border border-white/10 hover:border-white/25 text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-2 disabled:opacity-60"
        >
          <ArrowClockwise size={13} /> Refresh
        </button>
      </div>

      {!configured && (
        <div className="mb-6 card-surface p-4 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <WarningCircle size={20} className="text-amber-400 flex-shrink-0" weight="fill" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-100">QuickBooks integration is not configured</div>
              <div className="text-xs text-amber-200/80 mt-1">
                Set <code className="font-mono text-[11px]">QBO_CLIENT_ID</code>, <code className="font-mono text-[11px]">QBO_CLIENT_SECRET</code>, and <code className="font-mono text-[11px]">QBO_REDIRECT_URI</code> in the backend env vars, then restart. Get credentials at <a className="underline" href="https://developer.intuit.com" target="_blank" rel="noreferrer">developer.intuit.com</a>.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total connections</div>
          <div className="text-3xl font-display font-semibold mt-1" data-testid="admin-int-total-count">{conns.length}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Active</div>
          <div className="text-3xl font-display font-semibold mt-1 text-emerald-400" data-testid="admin-int-connected-count">
            {connectedCount}
          </div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Environment</div>
          <div className="text-lg font-semibold mt-1 text-neutral-300 truncate">
            {conns[0]?.environment || (configured ? "—" : "not configured")}
          </div>
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        {conns.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">
            <PlugsConnected size={40} className="mx-auto text-[#FF5A1F] mb-3" />
            <div className="text-lg text-white font-semibold">No QuickBooks connections yet</div>
            <div className="text-sm mt-1 max-w-md mx-auto">
              Pilots connect their own accounts from{" "}
              <span className="font-mono text-neutral-400">/app/integrations</span>.
              Their status will appear here.
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-neutral-500">
                <th className="text-left px-4 py-3">Pilot</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Realm ID</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Connected</th>
                <th className="text-left px-4 py-3">Last refresh</th>
              </tr>
            </thead>
            <tbody>
              {conns.map((c, i) => {
                const meta = STATUS_META[c.status] || STATUS_META.disconnected;
                const Icon = meta.icon;
                return (
                  <tr
                    key={`${c.user_id}-${c.realm_id}-${i}`}
                    className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`admin-int-row-${c.realm_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm text-white truncate max-w-[180px]">
                        {c.user_email || <span className="text-neutral-600">?</span>}
                      </div>
                      <div className="text-[10px] font-mono text-neutral-600 truncate max-w-[180px]">
                        {c.user_id?.slice(0, 8) || ""}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-neutral-300 truncate max-w-[200px] inline-flex items-center gap-1.5">
                        {c.company_name && <Buildings size={12} className="text-neutral-500" />}
                        {c.company_name || <span className="text-neutral-600">—</span>}
                      </div>
                      {c.user_company && (
                        <div className="text-[10px] text-neutral-500 truncate max-w-[200px]">
                          FP: {c.user_company}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-mono text-neutral-400">{c.realm_id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.cls} inline-flex items-center gap-1`}>
                        <Icon size={10} weight="fill" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-neutral-400">
                        {c.connected_at ? new Date(c.connected_at).toLocaleDateString() : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-neutral-500">
                        {c.last_refreshed_at
                          ? new Date(c.last_refreshed_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                          : "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
